"use client";
// beui.dev/components/motion/tabs
// Restyled for Bavimail (see DESIGN.md): every tab-like control in the app is one of
//   segment   - sharp inset well (same box as `Input`), dividers, sharp `bg-hb-raised` thumb.
//               Filter bars, range selectors. Vertical = borderless rows with dashed dividers,
//               meant to sit inside a panel column.
//   pill      - rounded-full track, raised thumb with a hairline ring, mono uppercase labels.
//   underline - hairline rule + 2px signal-orange line (Single-Wire: orange marks the active
//               item). `sm` = mono uppercase (in-panel tabs), `md` = Manrope bold (page tabs).
// The boxed variants are 34px (`sm`) / 46px (`md`) tall overall, matching `Input` sm / md.
// The indicator keeps beUI's `layoutId` glide on the shared, overshoot-free SPRING_LAYOUT.
//
// Bavimail additions (upstream API is otherwise unchanged):
//   Tabs        - `size` ("sm" | "md", default "md"), `orientation` ("horizontal" | "vertical"),
//                 `activationMode` ("automatic" | "manual"), generic value type.
//   TabsList    - `aria-label` / `aria-labelledby`.
//   TabsTrigger - `count` (trailing chip), `dot` (leading status dot, any CSS colour),
//                 `description` (second line), plus every native <button> attribute
//                 (`disabled`, `onMouseEnter`, `onFocus`, `onTouchStart`, ...).
//   Keyboard    - upstream shipped none: added roving tabindex, Arrow keys (Left/Right, or
//                 Up/Down when vertical, RTL aware), Home/End, `aria-orientation`, and
//                 tab <-> tabpanel `id` / `aria-controls` / `aria-labelledby` wiring.
//   Icons (svg) inside the active trigger turn signal orange.
// Focus rings are inset: the list sits in a scroll viewport that would clip an offset ring.
// `TabsContent` is optional: `Tabs` works as a plain controlled value switcher.

import { ChevronLeft, ChevronRight } from "lucide-react";
import { cancelFrame, frame, motion, MotionConfig, useReducedMotion } from "motion/react";
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { EASE_OUT, SPRING_LAYOUT } from "@/lib/ease";
import { cn } from "@/lib/utils";

export type TabsVariant = "pill" | "underline" | "segment";
export type TabsSize = "sm" | "md";
export type TabsOrientation = "horizontal" | "vertical";
export type TabsActivationMode = "automatic" | "manual";

type Ctx = {
  value: string;
  setValue: (v: string) => void;
  layoutId: string;
  baseId: string;
  variant: TabsVariant;
  size: TabsSize;
  orientation: TabsOrientation;
  activationMode: TabsActivationMode;
  panels: ReadonlySet<string>;
  registerPanel: (value: string) => () => void;
};

const TabsCtx = createContext<Ctx | null>(null);

function useTabs() {
  const ctx = useContext(TabsCtx);
  if (!ctx) throw new Error("Tabs.* must be used inside <Tabs>");
  return ctx;
}

const TAB_SELECTOR = '[role="tab"]';
const SELECTED_TAB_SELECTOR = '[role="tab"][aria-selected="true"]';

const slug = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, "_");
const tabId = (baseId: string, value: string) => `${baseId}tab-${slug(value)}`;
const panelId = (baseId: string, value: string) => `${baseId}panel-${slug(value)}`;

export interface TabsProps<T extends string = string> {
  defaultValue?: T;
  value?: T;
  onValueChange?: (v: T) => void;
  variant?: TabsVariant;
  /** Density. `sm` for toolbars and in-panel tabs, `md` for forms and page-level tabs. */
  size?: TabsSize;
  /** `vertical` stacks full-width rows; Up/Down arrows move between them. */
  orientation?: TabsOrientation;
  /** `manual`: arrow keys only move focus, Enter/Space selects (use when selecting is costly). */
  activationMode?: TabsActivationMode;
  children: ReactNode;
  className?: string;
}

export function Tabs<T extends string = string>({
  defaultValue,
  value,
  onValueChange,
  variant = "pill",
  size = "md",
  orientation = "horizontal",
  activationMode = "automatic",
  children,
  className,
}: TabsProps<T>) {
  const [internal, setInternal] = useState<string>(defaultValue ?? "");
  const [panels, setPanels] = useState<ReadonlySet<string>>(() => new Set());
  const layoutId = useId();
  const baseId = useId();
  const reduce = useReducedMotion();
  const controlled = value !== undefined;
  const current: string = controlled ? value : internal;
  const setValue = useCallback(
    (v: string) => {
      if (!controlled) setInternal(v);
      onValueChange?.(v as T);
    },
    [controlled, onValueChange],
  );
  const registerPanel = useCallback((panel: string) => {
    setPanels((previous) => (previous.has(panel) ? previous : new Set(previous).add(panel)));
    return () => {
      setPanels((previous) => {
        if (!previous.has(panel)) return previous;
        const next = new Set(previous);
        next.delete(panel);
        return next;
      });
    };
  }, []);
  const contextValue = useMemo<Ctx>(
    () => ({
      value: current,
      setValue,
      layoutId,
      baseId,
      variant,
      size,
      orientation,
      activationMode,
      panels,
      registerPanel,
    }),
    [
      current,
      setValue,
      layoutId,
      baseId,
      variant,
      size,
      orientation,
      activationMode,
      panels,
      registerPanel,
    ],
  );
  return (
    <MotionConfig transition={reduce ? { duration: 0 } : SPRING_LAYOUT}>
      <TabsCtx.Provider value={contextValue}>
        {/* layoutRoot: the indicator's layoutId measures in page coordinates, so
            inside fixed/scrolled containers it would replay scroll offsets as
            movement. The pill only ever travels within the list, so scoping
            projection to the Tabs wrapper is always correct. */}
        <motion.div layoutRoot className={className}>
          {children}
        </motion.div>
      </TabsCtx.Provider>
    </MotionConfig>
  );
}

// The boxed surface (border, fill, radius) of each variant. It normally sits on the list; when
// the list overflows it moves to the root so the scroll arrows live inside the same box.
const SURFACE_CLASS: Record<TabsVariant, string> = {
  pill: "rounded-full border border-hb-card bg-hb-surface",
  segment: "overflow-hidden rounded-sm border border-hb-card bg-hb-inset",
  underline: "",
};

const LIST_CLASS: Record<TabsOrientation, Record<TabsVariant, string>> = {
  horizontal: {
    pill: cn("inline-flex items-center gap-0.5 p-0.5", SURFACE_CLASS.pill),
    segment: cn("inline-flex items-stretch divide-x divide-hb-card", SURFACE_CLASS.segment),
    underline: "inline-flex items-stretch border-b border-hb-border",
  },
  vertical: {
    pill: cn(
      "flex flex-col items-stretch gap-0.5 p-0.5",
      "rounded-[18px] border border-hb-card bg-hb-surface",
    ),
    segment: "flex flex-col items-stretch divide-y divide-dashed divide-hb-card",
    underline: "flex flex-col items-stretch border-l border-hb-border",
  },
};

export function TabsList({
  children,
  className,
  wrapperClassName,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: {
  children: ReactNode;
  className?: string;
  wrapperClassName?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}) {
  const { variant, value, orientation, activationMode } = useTabs();
  const vertical = orientation === "vertical";
  const reduce = useReducedMotion();
  const rootRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const viewportId = useId();
  const [edges, setEdges] = useState({ overflow: false, left: false, right: false });

  const measure = useCallback(() => {
    const root = rootRef.current;
    const viewport = viewportRef.current;
    if (!root || !viewport) return;
    // Overlay controls do not reduce the viewport or change its scroll range.
    const overflow = !vertical && viewport.scrollWidth > root.clientWidth + 1;
    const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const rtl = getComputedStyle(viewport).direction === "rtl";
    // Modern browsers expose negative scrollLeft in RTL. Clamp rubber-banding.
    const fromLeft = Math.max(
      0,
      Math.min(max, rtl ? max + viewport.scrollLeft : viewport.scrollLeft),
    );
    const next = { overflow, left: fromLeft > 1, right: fromLeft < max - 1 };
    setEdges((previous) =>
      previous.overflow === next.overflow &&
      previous.left === next.left &&
      previous.right === next.right
        ? previous
        : next,
    );
  }, [vertical]);

  const reveal = useCallback(
    (tab: HTMLElement | null) => {
      const viewport = viewportRef.current;
      if (!viewport || !tab || vertical) return;
      const bounds = viewport.getBoundingClientRect();
      const item = tab.getBoundingClientRect();
      const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const rtl = getComputedStyle(viewport).direction === "rtl";
      const fromLeft = Math.max(
        0,
        Math.min(max, rtl ? max + viewport.scrollLeft : viewport.scrollLeft),
      );
      // Keep the selected/focused label clear of the arrows over the faded edges.
      const left = bounds.left + (fromLeft > 1 ? 36 : 0);
      const right = bounds.right - (fromLeft < max - 1 ? 36 : 0);
      const delta =
        item.left < left ? item.left - left : item.right > right ? item.right - right : 0;
      // Scroll only this viewport; scrollIntoView can also move the whole page.
      if (delta) viewport.scrollBy({ left: delta, behavior: reduce ? "instant" : "smooth" });
    },
    [reduce, vertical],
  );

  useLayoutEffect(() => {
    const root = rootRef.current;
    const viewport = viewportRef.current;
    const list = listRef.current;
    if (!root || !viewport || !list) return;
    const update = () => {
      measure();
      reveal(list.querySelector<HTMLElement>(SELECTED_TAB_SELECTOR));
    };
    const observer = new ResizeObserver(update);
    observer.observe(root);
    observer.observe(viewport);
    observer.observe(list);
    viewport.addEventListener("scroll", measure, { passive: true });
    update();
    return () => {
      observer.disconnect();
      viewport.removeEventListener("scroll", measure);
    };
  }, [measure, reveal]);

  useLayoutEffect(() => {
    // Children may change without a resize; controlled selection must also reveal.
    void children;
    void value;
    void edges.overflow;
    const list = listRef.current;
    measure();
    reveal(list?.querySelector<HTMLElement>(SELECTED_TAB_SELECTOR) ?? null);
    if (!list) return;
    // Roving tabindex: exactly one tab stop. Triggers render `tabIndex` themselves, but when no
    // trigger matches the value the first enabled tab has to stay reachable by keyboard.
    const tabs = Array.from(list.querySelectorAll<HTMLButtonElement>(TAB_SELECTOR));
    const stop =
      tabs.find((tab) => tab.getAttribute("aria-selected") === "true" && !tab.disabled) ??
      tabs.find((tab) => !tab.disabled);
    for (const tab of tabs) tab.tabIndex = tab === stop ? 0 : -1;
  }, [children, value, edges.overflow, measure, reveal]);

  useLayoutEffect(() => {
    if (variant === "underline") return;
    const list = listRef.current;
    if (!list) return;
    void children;
    const labels = Array.from(list.querySelectorAll<HTMLElement>("[data-tabs-label]"));
    const indicator = list.querySelector<HTMLElement>("[data-tabs-indicator]");
    const target = list.querySelector<HTMLElement>(SELECTED_TAB_SELECTOR);
    if (!indicator || !target || target.dataset.tabsValue !== value) {
      for (const label of labels) label.style.clipPath = "inset(0 100% 0 0)";
      return;
    }
    let frames = 0;
    let stillFrames = 0;
    let previous: DOMRect | undefined;
    const clamp = (n: number, max: number) => Math.max(0, Math.min(max, n));
    const syncClips = () => {
      const pill = (reduce ? target : indicator).getBoundingClientRect();
      // Read ALL geometry before writing ANY masks. A loop per tab interleaved
      // reads and writes, forcing the browser to flush styles repeatedly.
      // All four sides are clipped so the wipe also works for vertical lists.
      const clips = labels.map((label) => {
        const bounds = label.getBoundingClientRect();
        const left = clamp(pill.left - bounds.left, bounds.width);
        const right = clamp(bounds.right - pill.right, bounds.width);
        const top = clamp(pill.top - bounds.top, bounds.height);
        const bottom = clamp(bounds.bottom - pill.bottom, bounds.height);
        return left + right >= bounds.width || top + bottom >= bounds.height
          ? "inset(0 100% 0 0)"
          : `inset(${top}px ${right}px ${bottom}px ${left}px)`;
      });
      labels.forEach((label, index) => {
        if (label.style.clipPath !== clips[index]) label.style.clipPath = clips[index];
      });
      frames += 1;
      stillFrames =
        previous &&
        Math.abs(pill.left - previous.left) < 0.01 &&
        Math.abs(pill.right - previous.right) < 0.01 &&
        Math.abs(pill.top - previous.top) < 0.01 &&
        Math.abs(pill.bottom - previous.bottom) < 0.01
          ? stillFrames + 1
          : 0;
      previous = pill;
      if (reduce || (frames > 2 && stillFrames >= 2)) cancelFrame(syncClips);
    };
    // One shared pass after Motion paints the projected pill keeps every label
    // in sync, including labels crossed during a long or interrupted glide.
    frame.postRender(syncClips, true);
    return () => cancelFrame(syncClips);
  }, [value, children, variant, reduce]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const list = listRef.current;
    if (!list || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const origin =
      event.target instanceof HTMLElement ? event.target.closest<HTMLElement>(TAB_SELECTOR) : null;
    if (!origin) return;
    const tabs = Array.from(list.querySelectorAll<HTMLButtonElement>(TAB_SELECTOR)).filter(
      (tab) => !tab.disabled,
    );
    const index = tabs.findIndex((tab) => tab === origin);
    if (index === -1) return;
    const rtl = !vertical && getComputedStyle(list).direction === "rtl";
    const nextKey = vertical ? "ArrowDown" : rtl ? "ArrowLeft" : "ArrowRight";
    const previousKey = vertical ? "ArrowUp" : rtl ? "ArrowRight" : "ArrowLeft";
    let nextIndex: number;
    if (event.key === nextKey) nextIndex = (index + 1) % tabs.length;
    else if (event.key === previousKey) nextIndex = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = tabs.length - 1;
    else return;
    event.preventDefault();
    const next = tabs[nextIndex];
    next.focus();
    if (activationMode === "automatic" && next !== origin) next.click();
  };

  const scroll = (direction: number) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    viewport.scrollBy({
      left: direction * viewport.clientWidth * 0.8,
      behavior: reduce ? "instant" : "smooth",
    });
  };
  const controlClass = cn(
    "absolute inset-y-0 z-20 inline-flex w-9 items-center justify-center text-hb-muted",
    "transition-colors duration-[var(--motion-duration-fast)] hover:text-hb-heading",
    "focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-hb-ring",
    "disabled:pointer-events-none disabled:opacity-0",
  );
  const controlRadius = variant === "pill" ? "full" : "sm";

  return (
    <div
      ref={rootRef}
      className={cn(
        "relative isolate flex w-full max-w-full min-w-0 items-center",
        edges.overflow && SURFACE_CLASS[variant],
        wrapperClassName,
      )}
    >
      {edges.overflow && (
        <button
          type="button"
          aria-label="Scroll tabs left"
          aria-controls={viewportId}
          disabled={!edges.left}
          onClick={() => scroll(-1)}
          className={cn(
            controlClass,
            "left-0",
            controlRadius === "full" ? "rounded-l-full" : "rounded-l-sm",
          )}
        >
          <ChevronLeft size={16} aria-hidden="true" />
        </button>
      )}
      <motion.div
        ref={viewportRef}
        id={viewportId}
        layoutScroll
        className={cn(
          "w-full min-w-0",
          vertical
            ? "overflow-visible"
            : "overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          edges.overflow && "[border-radius:inherit]",
        )}
        style={
          edges.overflow
            ? {
                maskImage: `linear-gradient(to right, ${
                  edges.left ? "transparent, black 40px" : "black, black 0px"
                }, ${edges.right ? "black calc(100% - 40px), transparent" : "black 100%"})`,
              }
            : undefined
        }
        onFocusCapture={(event) => {
          if (event.target instanceof HTMLElement && event.target.getAttribute("role") === "tab") {
            reveal(event.target);
          }
        }}
      >
        <div
          ref={listRef}
          role="tablist"
          aria-orientation={orientation}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          onKeyDown={onKeyDown}
          className={cn(
            LIST_CLASS[orientation][variant],
            vertical ? "w-full" : "w-max",
            // The root carries the surface while overflowing; keep the border width so the
            // measured widths (and therefore the overflow state) cannot flap.
            edges.overflow && "rounded-none border-transparent bg-transparent",
            className,
          )}
        >
          {children}
        </div>
      </motion.div>
      {edges.overflow && edges.left && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 rounded-l-[inherit] backdrop-blur-[2px] [mask-image:linear-gradient(to_right,black,transparent)]"
        />
      )}
      {edges.overflow && edges.right && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 rounded-r-[inherit] backdrop-blur-[2px] [mask-image:linear-gradient(to_left,black,transparent)]"
        />
      )}
      {edges.overflow && (
        <button
          type="button"
          aria-label="Scroll tabs right"
          aria-controls={viewportId}
          disabled={!edges.right}
          onClick={() => scroll(1)}
          className={cn(
            controlClass,
            "right-0",
            controlRadius === "full" ? "rounded-r-full" : "rounded-r-sm",
          )}
        >
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

const TRIGGER_BASE_CLASS = cn(
  "relative items-center whitespace-nowrap bg-transparent outline-none",
  "transition-colors duration-[var(--motion-duration-fast)]",
  "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-hb-ring",
  "disabled:cursor-not-allowed disabled:opacity-40",
);

const TRIGGER_SIZE_CLASS: Record<TabsVariant, Record<TabsSize, string>> = {
  pill: { sm: "min-h-7 px-3 py-1", md: "min-h-10 px-5 py-1.5" },
  segment: { sm: "min-h-8 px-3 py-1", md: "min-h-11 px-4 py-1.5" },
  underline: { sm: "min-h-10 px-4 py-2", md: "min-h-11 px-4 pb-3 pt-2" },
};

// Vertical segment rows are navigation rows, not toolbar cells: roomier.
const VERTICAL_SEGMENT_SIZE_CLASS: Record<TabsSize, string> = {
  sm: "min-h-9 px-4 py-2",
  md: "min-h-12 px-5 py-3",
};

// `font-hb` / `font-mono-hb` are plain CSS classes (tailwind-merge cannot swap them), so the
// label carries exactly one of them and nothing above it sets a font.
const LABEL_CLASS: Record<TabsVariant, Record<TabsSize, string>> = {
  pill: {
    sm: "font-mono-hb text-[11px] uppercase tracking-[0.16em]",
    md: "font-mono-hb text-xs uppercase tracking-[0.16em]",
  },
  segment: {
    sm: "font-mono-hb text-xs font-semibold",
    md: "font-mono-hb text-[13px] font-semibold",
  },
  underline: {
    sm: "font-mono-hb text-[11px] uppercase tracking-[0.16em]",
    md: "font-hb text-[13px] font-bold",
  },
};

const TRIGGER_RADIUS_CLASS: Record<TabsVariant, string> = {
  pill: "rounded-full",
  segment: "rounded-none",
  underline: "rounded-sm",
};

const COUNT_CLASS = cn(
  "shrink-0 rounded-sm px-1.5 py-0.5 font-mono-hb text-[10px] font-normal leading-none",
  "normal-case tracking-normal tabular-nums",
);
const DESCRIPTION_CLASS = cn(
  "font-mono-hb text-[10px] font-normal normal-case leading-tight tracking-normal",
  "text-hb-muted",
);

export interface TabsTriggerProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "value" | "children" | "type" | "role"
> {
  value: string;
  children: ReactNode;
  className?: string;
  indicatorClassName?: string;
  /** Trailing count chip (filter bars). `0` renders; `null` / `undefined` hides it. */
  count?: ReactNode;
  /** Leading status dot: any CSS colour, e.g. `"var(--color-hb-success)"`. */
  dot?: string;
  /** Second line under the label (page-level tabs). The trigger grows to fit. */
  description?: ReactNode;
}

export function TabsTrigger({
  value,
  children,
  className,
  indicatorClassName,
  count,
  dot,
  description,
  onClick,
  ...rest
}: TabsTriggerProps) {
  const {
    value: current,
    setValue,
    layoutId,
    baseId,
    variant,
    size,
    orientation,
    panels,
  } = useTabs();
  const vertical = orientation === "vertical";
  const active = current === value;
  // React owns the initial mask only; TabsList synchronizes subsequent masks.
  const [initialClip] = useState(() => (active ? "inset(0)" : "inset(0 100% 0 0)"));

  // Rendered twice by the boxed variants: once in the resting look and once in the active look,
  // the latter masked to wherever the indicator currently is.
  const renderContent = (activeLook: boolean) => {
    const row = (
      <span className={cn("items-center gap-2", vertical ? "flex w-full" : "inline-flex")}>
        {dot ? (
          <span
            aria-hidden="true"
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: dot }}
          />
        ) : null}
        <span
          className={cn(
            "items-center gap-2",
            vertical ? "flex min-w-0 flex-1 justify-between text-left" : "inline-flex",
            LABEL_CLASS[variant][size],
          )}
        >
          {children}
        </span>
        {count !== undefined && count !== null ? (
          <span
            className={cn(
              COUNT_CLASS,
              activeLook ? "bg-hb-card text-hb-text" : "bg-hb-raised text-hb-faint",
            )}
          >
            {count}
          </span>
        ) : null}
      </span>
    );
    if (description === undefined || description === null) return row;
    return (
      <span
        className={cn(
          "flex-col items-start gap-0.5 text-left",
          vertical ? "flex w-full" : "inline-flex",
        )}
      >
        {row}
        <span className={DESCRIPTION_CLASS}>{description}</span>
      </span>
    );
  };

  const shared = {
    ...rest,
    type: "button" as const,
    role: "tab" as const,
    id: rest.id ?? tabId(baseId, value),
    "aria-selected": active,
    "aria-controls":
      rest["aria-controls"] ?? (panels.has(value) ? panelId(baseId, value) : undefined),
    tabIndex: active ? 0 : -1,
    "data-state": active ? "active" : "inactive",
    "data-tabs-value": value,
    onClick: (event: MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (!event.defaultPrevented) setValue(value);
    },
  };

  if (variant === "underline") {
    return (
      <button
        {...shared}
        className={cn(
          TRIGGER_BASE_CLASS,
          "isolate shrink-0",
          vertical ? "-ml-px flex w-full justify-start" : "-mb-px inline-flex justify-center",
          TRIGGER_SIZE_CLASS.underline[size],
          TRIGGER_RADIUS_CLASS.underline,
          active ? "text-hb-heading [&_svg]:text-hb-accent" : "text-hb-muted hover:text-hb-text",
          className,
        )}
      >
        {renderContent(active)}
        {active ? (
          <motion.span
            layoutId={layoutId}
            layout
            className={cn(
              "absolute bg-hb-accent",
              vertical ? "inset-y-0 left-0 w-0.5" : "inset-x-0 bottom-0 h-0.5",
              indicatorClassName,
            )}
          />
        ) : null}
      </button>
    );
  }

  const pill = variant === "pill";
  const verticalSegment = vertical && variant === "segment";

  return (
    // A flex cell (not a block) so the button has no inline strut and fills the cell height.
    <div className="relative flex shrink-0 items-stretch">
      {active ? (
        <motion.span
          data-tabs-indicator=""
          layoutId={layoutId}
          layout
          // Inline so Motion can correct both for the scale it applies while gliding.
          style={{
            borderRadius: pill ? 9999 : 0,
            boxShadow: pill
              ? "0 0 0 1px color-mix(in oklab, var(--hb-heading) 6%, transparent)"
              : undefined,
          }}
          className={cn("absolute inset-0 bg-hb-raised", indicatorClassName)}
        />
      ) : null}
      <button
        {...shared}
        className={cn(
          TRIGGER_BASE_CLASS,
          "z-10",
          vertical ? "flex w-full justify-start" : "inline-flex justify-center",
          verticalSegment ? VERTICAL_SEGMENT_SIZE_CLASS[size] : TRIGGER_SIZE_CLASS[variant][size],
          TRIGGER_RADIUS_CLASS[variant],
          pill || verticalSegment
            ? "text-hb-muted hover:text-hb-text"
            : "text-hb-faint hover:text-hb-subtle",
          className,
        )}
      >
        {renderContent(false)}
        <span
          data-tabs-label=""
          aria-hidden="true"
          inert
          className={cn(
            "pointer-events-none absolute inset-0 flex items-center text-hb-heading",
            "[padding:inherit] [&_svg]:text-hb-accent",
            vertical ? "justify-start" : "justify-center",
          )}
          style={{ clipPath: initialClip }}
        >
          {renderContent(true)}
        </span>
      </button>
    </div>
  );
}

export function TabsContent({
  value,
  children,
  className,
}: {
  value: string;
  children: ReactNode;
  className?: string;
}) {
  const { value: current, baseId, orientation, registerPanel } = useTabs();
  const reduce = useReducedMotion();
  const active = current === value;
  useLayoutEffect(() => registerPanel(value), [registerPanel, value]);
  const aria = {
    role: "tabpanel" as const,
    id: panelId(baseId, value),
    "aria-labelledby": tabId(baseId, value),
  };
  // Inactive panels stay mounted but hidden, so their content (e.g. source
  // code) is present in the server-rendered HTML for crawlers and assistive
  // tech, instead of being dropped from the DOM.
  if (!active) {
    return (
      <div {...aria} hidden className={className}>
        {children}
      </div>
    );
  }
  return (
    <motion.div
      key={value}
      {...aria}
      tabIndex={0}
      initial={{ opacity: 0, y: reduce ? 0 : 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: EASE_OUT }}
      className={cn(
        "outline-none focus-visible:ring-2 focus-visible:ring-hb-ring",
        orientation === "horizontal" && "mt-4",
        className,
      )}
    >
      {children}
    </motion.div>
  );
}
